import { CSSProperties, useMemo } from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import formatCentsToLocaleCurrency from 'src/utils/finance';

import { getDefaultCurrencyInputStyledStyles } from '../inputs/NPICurrencyInput/NPICurrencyInput.styles';

export enum DisplayCurrencyStyleVariants {
    NONE = 'NONE',
    TYPOGRAPHY = 'TYPOGRAPHY',
    INPUT = 'INPUT',
    INPUT_SYMBOL_RIGHT = 'INPUT_SYMBOL_RIGHT',
}

const defaultStyleVariant = DisplayCurrencyStyleVariants.TYPOGRAPHY;
interface DisplayCurrencyProps {
    localeLanguage: string;
    centsAmount: number;
    currencyAmount?: number;
    style?: CSSProperties;
    disableStyling: boolean;
    styleVariant?: DisplayCurrencyStyleVariants;
    usersCurrency?: string;
}

const DisplayCurrency = ({
    localeLanguage,
    centsAmount,
    currencyAmount,
    style = {
        fontSize: '14px',
        fontWeight: '400',
    },
    disableStyling,
    styleVariant = defaultStyleVariant,
    usersCurrency,
}: DisplayCurrencyProps) => {
    const theme = useTheme();

    const { initialCurrencyFormatted, formattedAmount, cachedStyle, error, initialCurrency } =
        useMemo(() => {
            try {
                return {
                    initialCurrencyFormatted: Number.isFinite(currencyAmount)
                        ? currencyAmount === 0
                            ? `${usersCurrency}${currencyAmount.toFixed(2)}`
                            : `${usersCurrency}${currencyAmount}`
                        : null,
                    initialCurrency: Number.isFinite(currencyAmount) ? currencyAmount : null,
                    formattedAmount: formatCentsToLocaleCurrency(centsAmount, localeLanguage),
                    cachedStyle: style?.color
                        ? style
                        : { ...style, color: theme.palette.text.primary },
                };
            } catch (error) {
                return {
                    error: error.message,
                    cachedStyle: {
                        ...style,
                        fontSize: '13px',
                        color: theme.palette.error.main,
                    },
                };
            }
        }, [
            centsAmount,
            currencyAmount,
            localeLanguage,
            style,
            theme.palette.error.main,
            theme.palette.text.primary,
            usersCurrency,
        ]);

    const value = useMemo(() => {
        return (
            initialCurrencyFormatted ||
            (initialCurrency === 0 ? initialCurrency.toFixed(2) : initialCurrency) ||
            error ||
            formattedAmount
        );
    }, [error, formattedAmount, initialCurrency, initialCurrencyFormatted]);

    if (disableStyling || styleVariant === DisplayCurrencyStyleVariants.NONE) {
        return value;
    }

    if (styleVariant === DisplayCurrencyStyleVariants.TYPOGRAPHY) {
        return (
            <Typography data-testid="display-currency" sx={cachedStyle}>
                {value}
            </Typography>
        );
    }

    const sharedStylesForInputVariants = {
        background: '#fafafa',
        borderColor: `thin solid ${theme.palette.border.disabled}`,
        width: '100%',
        height: '100%',
        display: 'flex',
        padding: '15px',
    };

    const sharedStylesForValueInputVariants = {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        minWidth: '0',
        alignItems: 'center',
        padding: '4px 0 5px',
        font: 'inherit',
    };

    if (styleVariant === DisplayCurrencyStyleVariants.INPUT) {
        // similar style to inputs for transaction charges non-editable
        return (
            <Box>
                <Typography
                    sx={{
                        ...getDefaultCurrencyInputStyledStyles(theme, false, true),
                        ...sharedStylesForInputVariants,
                    }}
                >
                    <Box sx={sharedStylesForValueInputVariants}>{value}</Box>
                </Typography>
            </Box>
        );
    }

    if (styleVariant === DisplayCurrencyStyleVariants.INPUT_SYMBOL_RIGHT) {
        // similar style to inputs for transaction charges non-editable
        return (
            <Box>
                <Typography
                    sx={{
                        ...getDefaultCurrencyInputStyledStyles(theme, false, true),
                        ...sharedStylesForInputVariants,
                    }}
                >
                    <Box sx={sharedStylesForValueInputVariants}>{initialCurrency}</Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%',
                            height: '100%',
                            alignItems: 'center',
                            alignContent: 'right',
                            marginRight: '8px',
                            color: '#b4b4b3',
                            padding: '4px 0 5px',
                        }}
                    >
                        {usersCurrency}
                    </Box>
                </Typography>
            </Box>
        );
    }
};
export default DisplayCurrency;
